import { useEffect } from "react";
import { io } from "socket.io-client";

const WS_URL = import.meta.env.VITE_APP_INBOX_WS_URL;

// suggestion_auto_sent fires once an AI reply is linked to its suggestion,
// which is when Awaiting reply can count it as an answer.
type InboxSocketEventName =
  | "new_message"
  | "conversation_update"
  | "suggestion_auto_sent";

const INBOX_SOCKET_EVENTS: InboxSocketEventName[] = [
  "new_message",
  "conversation_update",
  "suggestion_auto_sent"
];

type Listener = (event: InboxSocketEventName) => void;

const listeners = new Set<Listener>();

// Every Inbox socket republishes here, so a page can listen without opening
// its own socket when one is already up.
export const subscribeInboxSocketEvents = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const publishInboxSocketEvent = (event: InboxSocketEventName) => {
  listeners.forEach((listener) => listener(event));
};

export const useInboxEventsSocket = ({
  accessToken,
  enabled
}: {
  accessToken: string | undefined;
  enabled: boolean;
}) => {
  useEffect(() => {
    if (!accessToken || !enabled || !WS_URL) return;

    const socket = io(`${WS_URL}/portal/inbox`, {
      auth: { token: accessToken },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: Infinity
    });

    INBOX_SOCKET_EVENTS.forEach((event) =>
      socket.on(event, () => publishInboxSocketEvent(event))
    );

    return () => {
      socket.disconnect();
    };
  }, [accessToken, enabled]);
};
